import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import App from "./App";
import "./index.css";

/* -------------------------------------------------------
   Suite-token auth — ADMIN ONLY.
   The Suite embeds this app with ?suite_token=xxx in the URL.
   We decode the JWT client-side for display (email, roles)
   and restrict access to admin users only.
------------------------------------------------------- */

function decodeJwtPayload(token) {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
    return payload;
  } catch {
    return null;
  }
}

function SuiteGate() {
  const [state, setState] = useState({ loading: true, suiteToken: null, user: null, error: "" });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("suite_token");

    if (!token) {
      setState({ loading: false, suiteToken: null, user: null, error: "no_token" });
      return;
    }

    const payload = decodeJwtPayload(token);
    if (!payload) {
      setState({ loading: false, suiteToken: null, user: null, error: "invalid_token" });
      return;
    }

    if (payload.exp && payload.exp < Math.floor(Date.now() / 1000)) {
      setState({ loading: false, suiteToken: null, user: null, error: "expired_token" });
      return;
    }

    const roles = payload.roles || payload?.app_metadata?.roles || [];
    const normalizedRoles = roles.map((r) => String(r).toLowerCase());

    // ADMIN + ACCELERATOR + MENTORSHIP can access
    const hasAccess = normalizedRoles.includes("admin") || normalizedRoles.includes("accelerator") || normalizedRoles.includes("mentorship");

    if (!hasAccess) {
      setState({ loading: false, suiteToken: null, user: null, error: "no_access" });
      return;
    }

    const user = {
      email: payload.email || payload.sub || "",
      app_metadata: { roles },
    };

    const cleanUrl = window.location.pathname;
    window.history.replaceState({}, "", cleanUrl);

    setState({ loading: false, suiteToken: token, user, error: "" });
  }, []);

  // Listen for token refresh from parent Suite
  useEffect(() => {
    const handler = (e) => {
      const d = e.data;
      if (!d || typeof d !== "object") return;
      if (d.type !== "SUITE_TOKEN_REFRESH" || d.source !== "creative-seller-suite") return;
      if (!d.token) return;
      const payload = decodeJwtPayload(d.token);
      if (!payload) return;
      const roles = payload.roles || payload?.app_metadata?.roles || [];
      const normalizedRoles = roles.map((r) => String(r).toLowerCase());
      const hasAccess = normalizedRoles.includes("admin") || normalizedRoles.includes("accelerator") || normalizedRoles.includes("mentorship");
      if (!hasAccess) return;
      const user = {
        email: payload.email || payload.sub || "",
        app_metadata: { roles },
      };
      setState({ loading: false, suiteToken: d.token, user, error: "" });
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  const gateStyle = {
    minHeight: "100vh",
    display: "grid",
    placeItems: "center",
    background: "#0a0a14",
    color: "#e2e8f0",
    fontFamily: "'DM Sans', system-ui, sans-serif",
  };

  const cardStyle = {
    textAlign: "center",
    maxWidth: 520,
    padding: "40px 32px",
  };

  if (state.loading) {
    return (
      <div style={gateStyle}>
        <div style={cardStyle}>
          <div style={{
            width: 40, height: 40, margin: "0 auto 16px",
            border: "3px solid rgba(99,102,241,0.3)", borderTopColor: "#6366f1",
            borderRadius: "50%", animation: "spin 0.8s linear infinite",
          }} />
          <div style={{ color: "#94a3b8" }}>Loading…</div>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (state.error === "no_token") {
    return (
      <div style={gateStyle}>
        <div style={cardStyle}>
          <h2 style={{ marginBottom: 8 }}>Access through Suite required</h2>
          <p style={{ color: "#94a3b8" }}>This app can only be accessed through the Creative Seller Suite.</p>
          <p style={{ color: "#64748b", fontSize: 14 }}>Please open the Mockup Studio from your Suite dashboard.</p>
        </div>
      </div>
    );
  }

  if (state.error === "expired_token") {
    return (
      <div style={gateStyle}>
        <div style={cardStyle}>
          <h2 style={{ marginBottom: 8 }}>Session expired</h2>
          <p style={{ color: "#94a3b8" }}>Your session has expired. Please go back to the Suite and reopen the Mockup Studio.</p>
        </div>
      </div>
    );
  }

  if (state.error === "no_access") {
    return (
      <div style={gateStyle}>
        <div style={cardStyle}>
          <h2 style={{ marginBottom: 8 }}>Access restricted</h2>
          <p style={{ color: "#94a3b8" }}>This app is currently only available to administrators.</p>
          <p style={{ color: "#64748b", fontSize: 14 }}>If you believe this is a mistake, please contact support.</p>
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div style={gateStyle}>
        <div style={cardStyle}>
          <h2 style={{ marginBottom: 8 }}>Authentication error</h2>
          <p style={{ color: "#94a3b8" }}>Could not verify your access. Please try again from the Suite.</p>
        </div>
      </div>
    );
  }

  return <App suiteToken={state.suiteToken} suiteUser={state.user} />;
}

const container = document.getElementById("root");
ReactDOM.render(<SuiteGate />, container);

// DEV-ONLY privacy-coverage overlay — self-audits this app's own DOM (the Suite's
// overlay can't see into this cross-origin iframe). Gated so it strips from prod.
if (process.env.NODE_ENV === "development") {
  import("./privacyAudit").then((m) => m.initPrivacyAudit());
}
